import { computed, ref, watch } from 'vue'
import { api } from '@/global/services/api'
import { isArray, isFunction, isObject } from '@/global/utils/utils'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { useTableService, DEFAULT_SELECTION_CHANGE_DELAY } from '@/global/composables/table/useTableService'
import { cancelAction, showDetailsAction } from '@/global/components/table/helpers/tableActions'
import { FORMATS } from '@/global/services/date'
import WorkOrdersMapPanel from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/components/WorkOrdersMapPanel.vue'
import { useWorkOrderMapData } from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/composables/useWorkOrderMapData'
import {
  toControlPointsLabel,
  toCoveragePercentLabel,
  toDatetimeLabel,
  toKilometresLabel,
  toKindLabel
} from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/composables/workOrderData'
import { workOrdersScreenView } from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/composables/useScreenView'
import { BASES_AUTOCOMPLETE_ENDPOINT } from '@/modules/road-maintenance-module/winter-service-v2/bases/config/constants'
import { TASKS_AUTOCOMPLETE_ENDPOINT } from '@/modules/road-maintenance-module/winter-service-v2/tasks/config/constants'
import {
  WINTER_SERVICE_MODULE,
  WINTER_SERVICE_VEHICLES_AUTOCOMPLETE
} from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  WORK_ORDERS_ENDPOINT,
  WORK_ORDERS_OVERVIEW_TABLE_KEY,
  WORK_ORDER_CANCEL_ENDPOINT,
  WORK_ORDER_CLOSED_STATUSES,
  WORK_ORDERS_OVERVIEW_TRANSLATION_NAMESPACE as NS,
  WORK_ORDERS_OVERVIEW_UI_VIEW,
  WORK_ORDER_DETAILS_ROUTE,
  WORK_ORDER_STATUSES_ENDPOINT
} from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/config/constants'

const { buildConfig } = useTableService()

const API_CONFIG = {
  get: {
    module: WINTER_SERVICE_MODULE,
    route: WORK_ORDERS_ENDPOINT
  }
}

/**
 * The work orders overview: the orders listed beside the map their routes are drawn
 * on.
 *
 * A row click marks the order rather than opening it - marking is what tells the map
 * which routes and which reported irregularities are worth looking at. Opening an order
 * is the double click on its row, with the row's action menu as the other way in. The
 * listing carries no geometry, so both are fetched off the marked rows.
 */
export function useTable (options) {
  const {
    trans,
    router,
    openInfoDialogFn,
    closeInfoDialogFn,
    setInfoDialogLoaderFn
  } = options || {}

  // What the map draws, fetched the same way the order's own screen fetches it - see
  // `useWorkOrderMapData`.
  const {
    streets: markedStreets,
    irregularities: markedIrregularities,
    vehicles: markedVehicles,
    isLoading: isLoadingMapData,
    load: loadMapData,
    clear: clearMapData
  } = useWorkOrderMapData()

  // The rows the user marked.
  const markedRows = ref([])

  const fetchTableData = () => {
    const { fetchData } = registeredTableHandlers?.[WORK_ORDERS_OVERVIEW_TABLE_KEY] || {}
    if (!isFunction(fetchData)) {
      return
    }
    fetchData()
  }

  /**
   * What the marked orders bring to the map.
   *
   * Only while there is a map to draw them on. In the table-only view the routes would be
   * fetched - and waited for, behind the loader - for a panel that is not on the screen;
   * the watch below asks for them the moment the map is given room again.
   */
  const loadPanelData = async () => {
    if (!workOrdersScreenView.isPanelVisible.value) {
      clearMapData()

      return
    }

    return loadMapData(markedRows.value)
  }

  const onMarkedRowsChange = (highlighted) => {
    markedRows.value = isArray(highlighted) ? highlighted : []

    return loadPanelData()
  }

  // Catching up on what was marked while the map had no room. Hiding it drops nothing, so
  // a switch away and straight back costs no request.
  watch(
    workOrdersScreenView.isPanelVisible,
    (isVisible) => isVisible ? loadPanelData() : undefined
  )

  const onShowWorkOrderDetails = (event) => {
    const { id } = event?.data || {}
    if (!id) {
      return
    }

    router.push({
      name: WORK_ORDER_DETAILS_ROUTE,
      params: {
        id: String(id)
      }
    })
  }

  // The listing reports an order nested - the vehicle, the driver, the task and the
  // status each as their own object, the progress under `locations` and `coverage` -
  // so the columns read their value off those.
  const cellsConfig = computed(() => ({
    vehicle: {
      custom_display_value: (data) => data?.vehicle?.registration || ''
    },
    driver: {
      custom_display_value: (data) => data?.driver?.full_name || ''
    },
    started_at: {
      custom_display_value: (data) => toDatetimeLabel(data?.started_at) || '-'
    },
    finished_at: {
      custom_display_value: (data) => toDatetimeLabel(data?.finished_at) || '-'
    },
    control_points: {
      custom_display_value: (data) => toControlPointsLabel(data)
    },
    kilometres_travelled: {
      custom_display_value: (data) => toKilometresLabel(data)
    },
    coverage_percent: {
      custom_display_value: (data) => toCoveragePercentLabel(data)
    },
    irregularity_count: {
      custom_display_value: (data) => data?.irregularity_count ?? 0
    },
    kind: {
      custom_display_value: (data) => toKindLabel(trans, data?.kind)
    },
    planned_vehicle: {
      custom_display_value: (data) => data?.planned_vehicle?.registration || '-'
    },
    task: {
      custom_display_value: (data) => data?.task?.id ?? '-'
    },
    // The same pill every other status column in the app draws - see BasesIndex, the
    // temporary regulations listings, the aggregator tables. The listing endpoint already
    // hands the status over translated and carrying the colours the status seeder gives
    // it (`RoadMaintenanceStatusSeeder`, `StatusType::WORK_ORDER`), so the cell is the
    // status object itself: StatusIndicator reads its label off `description` and the
    // dot's fill and outline off `color`.
    status: {
      type: 'status',
      displayValue: 'description',
      colorObjectKey: 'color',
      textColorKey: 'text',
      backgroundColorKey: 'background',
      custom_display_value: (data) => isObject(data?.status) ? data.status : null
    }
  }))

  // Every column is shown by default: `always_visible` for the ones the listing makes no
  // sense without, `initial_value` for the rest, which the user can hide from the columns
  // menu - and that choice, once made, is remembered per user and wins over this.
  const headers = computed(() => ([
    {
      label: trans(`${NS}.id`),
      key: 'id',
      always_visible: true
    },
    {
      // Second, beside the id rather than at the far end of the row: it is what an order
      // is read by before anything else, and with a dozen columns after it the far end is
      // off the screen. Kept next to the id also keeps it put - the hideable columns all
      // sit to the right of here, so a status at the end moved every time one of them was
      // switched off.
      label: trans(`${NS}.status`),
      key: 'status',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.vehicle`),
      key: 'vehicle',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.driver`),
      key: 'driver',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.taken_at`),
      key: 'started_at',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.finished_at`),
      key: 'finished_at',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.control_points`),
      key: 'control_points',
      initial_value: true
    },
    {
      // Driven out of required, the same pair the order's "Podaci o izvršenju" tab shows.
      label: trans(`${NS}.kilometres_travelled`),
      key: 'kilometres_travelled',
      initial_value: true
    },
    {
      label: trans(`${NS}.cleaned`),
      key: 'coverage_percent',
      initial_value: true
    },
    {
      label: trans(`${NS}.irregularities`),
      key: 'irregularity_count',
      initial_value: true
    },
    {
      label: trans(`${NS}.order_type`),
      key: 'kind',
      initial_value: true
    },
    {
      label: trans(`${NS}.planned_vehicle`),
      key: 'planned_vehicle',
      initial_value: true
    },
    {
      label: trans(`${NS}.task_id`),
      key: 'task',
      initial_value: true
    }
  ]))

  // Only what the listing endpoint understands. It matches on ids and plain dates rather
  // than text, so each of these is a picker or a date.
  //
  // The order is the layout: the dialog lays the fields out two to a row, in the order
  // they are listed. The period comes first because it is what a dispatcher narrows by
  // before anything else, and its two ends have to sit side by side - listed anywhere but
  // at the head of an even list, `from` and `to` ended up on separate rows.
  const filtersConfig = computed(() => ({
    fields: [
      {
        // The endpoint reads these against the moment the order was taken over, so
        // no time part is sent.
        key: 'from',
        label: trans(`${NS}.date_from`),
        type: 'datetime-picker',
        subType: 'date',
        appendIcon: 'mdi-calendar-month',
        placeholder: FORMATS.date
      },
      {
        key: 'to',
        label: trans(`${NS}.date_to`),
        type: 'datetime-picker',
        subType: 'date',
        appendIcon: 'mdi-calendar-month',
        placeholder: FORMATS.date
      },
      {
        // Straight after the period: it is the next thing a dispatcher narrows by -
        // what is still running, what was called off - and it keeps `from`/`to` as the
        // pair at the head of the list.
        key: 'status_id',
        label: trans(`${NS}.status`),
        type: 'autocomplete',
        autocomplete_options: {
          module: WINTER_SERVICE_MODULE,
          route: WORK_ORDER_STATUSES_ENDPOINT
        }
      },
      {
        key: 'vehicle_id',
        label: trans(`${NS}.vehicle`),
        type: 'autocomplete',
        autocomplete_options: {
          ...WINTER_SERVICE_VEHICLES_AUTOCOMPLETE
        }
      },
      {
        key: 'driver_id',
        label: trans(`${NS}.driver`),
        type: 'autocomplete',
        autocomplete_options: {
          module: 'people',
          route: 'people/autocomplete'
        }
      },
      {
        // Picked by name rather than typed as an id: the listing shows a task's id, but
        // that is not what anyone knows a task by.
        key: 'task_id',
        label: trans(`${NS}.task`),
        type: 'autocomplete',
        autocomplete_options: {
          module: WINTER_SERVICE_MODULE,
          route: TASKS_AUTOCOMPLETE_ENDPOINT
        }
      },
      {
        key: 'base_id',
        label: trans(`${NS}.base`),
        type: 'autocomplete',
        autocomplete_options: {
          module: WINTER_SERVICE_MODULE,
          route: BASES_AUTOCOMPLETE_ENDPOINT
        }
      }
    ]
  }))

  /**
   * Calls an order off. It is not deleted: it stays in the listing under a cancelled
   * status, because an order that was raised and called off is part of the night's
   * record.
   */
  const cancelWorkOrder = async (event) => {
    try {
      const { id } = event?.data || {}

      if (!id) {
        return
      }

      setInfoDialogLoaderFn?.(true)
      await api(WINTER_SERVICE_MODULE).post(WORK_ORDER_CANCEL_ENDPOINT(id))
      closeInfoDialogFn?.()
      fetchTableData()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setInfoDialogLoaderFn?.(false)
    }
  }

  const onCancelWorkOrder = (event) => {
    const { id } = event?.data || {}

    if (!isFunction(openInfoDialogFn) || !id) {
      return
    }

    openInfoDialogFn({
      // The order's id in the title: the listing holds a night's worth of orders that
      // read alike - the vehicle, the driver, the task - so the confirmation has to say
      // which one of them is about to be called off.
      title: `${trans(`${NS}.cancel_confirmation_title`)} - ${id}`,
      message: trans(`${NS}.cancel_confirmation_message`),
      onConfirm: () => cancelWorkOrder(event)
    })
  }

  /**
   * An order that has been driven, or called off already, has nothing left to call off.
   *
   * One that is being driven right now has: a crew can be sent home mid-shift. It is not
   * the end of it either - a device that keeps reporting against a called-off order puts
   * it back in progress (see the backend's coverage report handler).
   */
  const canCancelWorkOrder = (event) =>
    !WORK_ORDER_CLOSED_STATUSES.includes(event?.data?.status?.name)

  // Work orders are raised from the plan and closed by the drivers' devices, so there is
  // nothing to create, edit or delete here - only inspect, and call off one that is not
  // going to be driven after all.
  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onShowWorkOrderDetails),
    cancelAction(trans, onCancelWorkOrder, {
      label: trans(`${NS}.cancel_order`),
      visible: canCancelWorkOrder
    })
  ]))

  // The screen-split picker, the same one the tasks and requests listings carry. Always
  // offered - it is how the map is asked for a share of the screen, or the whole of it.
  const screenViewComponents = computed(() => ([
    {
      component: workOrdersScreenView.toggle,
      // DataTable renders the grid-switcher and column-visibility icons ahead of this
      // slot and gives them no order of their own, so any negative value pulls this in
      // front of them.
      order: -1
    }
  ]))

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance/winter_service2.work_orders')
      },
      {
        text: trans('road-maintenance/winter_service2/work_orders.overview'),
        bolded: true
      }
    ]
  }))

  const config = computed(() => ({
    tableConfig: buildConfig({
      key: WORK_ORDERS_OVERVIEW_TABLE_KEY,
      uiView: WORK_ORDERS_OVERVIEW_UI_VIEW,
      apiConfig: API_CONFIG,
      // Clicking a row is what picks it out for the map. Opening an order is the double
      // click, and the row's action menu keeps it reachable either way.
      rowDblClick: onShowWorkOrderDetails,
      // A click on a row shows it on the map beside the table
      rowClickIndicator: 'map',
      // Picking fetches the picked orders' routes and their coverage, the reports along
      // them and where their vehicles are, none of which the map on the right can draw
      // until it arrives. The overlay covers the whole screen the two share, so neither
      // half is read while it is behind.
      showLoader: isLoadingMapData.value,
      search_and_buttons_config: {
        // Free text over the columns the table shows as text - the order's id, the
        // vehicle that drove it, its driver, the vehicle it was planned with and the
        // order's own description. The filters dialog stays for the narrower,
        // per-field lookups.
        search_field_config: {
          placeholder: trans('base.tracker_search'),
          prependInnerIcon: 'mdi-magnify'
        },
        showNewBtn: false
      },
      filters_row_end_components: screenViewComponents.value,
      // Gone only in the map-only view, where the map is the whole screen. What is marked
      // survives the switch, so orders marked beside the listing are what the full-width
      // map draws.
      hideTable: !workOrdersScreenView.isTableVisible.value,
      rowsSelection: {
        // Picking rows is what this table's row click is for - it is how the map is
        // told which orders to draw - so the checkbox column is not needed for it.
        showCheckboxes: false,
        // Rows are keyed by the order's id rather than by where they sit, so the map
        // and the table cannot disagree about which order is picked.
        rememberSelectionByDataProperty: 'id',
        // What a pick drives here is a run of requests, and the loader they raise
        // covers the very rows the user is still clicking - so the clicking is let
        // finish first and the map is asked for once, for all of them.
        changeDelay: DEFAULT_SELECTION_CHANGE_DELAY,
        selectionsChange: ({ data }) => onMarkedRowsChange(data)
      },
      sideTableComponent: {
        component: WorkOrdersMapPanel,
        visible: workOrdersScreenView.isPanelVisible.value,
        streets: markedStreets.value,
        irregularities: markedIrregularities.value,
        // Where the marked orders' vehicles are standing right now, which is the one
        // thing on this map that is not about what has already happened.
        vehicles: markedVehicles.value
      },
      globalStyle: workOrdersScreenView.globalStyle.value,
      tableConfig: {
        cellsConfig: cellsConfig.value,
        headers: headers.value,
        actionsConfig: actionsConfig.value,
        filtersConfig: filtersConfig.value
      }
    }),
    breadcrumbs: breadcrumbs.value
  }))

  return {
    config,
    fetchTableData
  }
}
